if (!customElements.get('searchspring-recommendations')) {
  class SearchspringRecommendations extends HTMLElement {
    constructor() {
      super()
      this.products = []
    }

    connectedCallback() {
      // Re-attached after cart drawer HTML swap — keep existing session
      if (this._initialized) return

      this.setupMetadata()
      this._initialized = true
      this.init()
    }

    setupMetadata() {
      this.settings = JSON.parse(
        this.querySelector('.settings').textContent.trim()
      )
      this.siteId = this.settings.siteId
      this.items = this.settings?.items || ''
      this.recommendation_type = this.settings.recommendation_type
      this.exclusions = this.settings.product_excludes
      this.limit = this.settings.limit || 8
      this.sectionName = this.getAttribute('section-name') || 'api-product-card'
      this.placement = this.settings.placement
      if (this.placement === 'mini-cart') {
        this.productKey = 'cart'
      } else {
        this.productKey = 'products'
      }
      this.shopperId = this.getAttribute('data-shopper-id') || null
      this.responseId = null
    }

    async init() {
      const products = await this.fetchRecommendations()

      if (!this.isConnected) return

      if (!products.length) {
        this.style.display = 'none'
        return
      }

      this.products = products
      this.render()

      const slides = await this.buildSlides(products)

      if (!this.isConnected) return

      this.querySelector('.splide__list').innerHTML = slides
      this.querySelector('.js-recommendations-slider').classList.add(
        'is-loaded'
      )
      this.classList.add('is-loaded')
      this.initCarousel()
    }

    /**
     * Swaps in recommendations for new inputs without unmounting the carousel.
     * The current slides stay on screen until the replacements are ready.
     *
     * Resolves true only once those slides are on screen. `items` is committed
     * on the same terms, so a refresh that bails leaves the element describing
     * what it is still showing and the next attempt isn't mistaken for a no-op.
     */
    async refresh(items = '') {
      const nextItems = String(items || '')
      if (nextItems === this.items) return true

      const token = (this.refreshToken || 0) + 1
      this.refreshToken = token

      const products = await this.fetchRecommendations(nextItems)
      if (token !== this.refreshToken || !this.isConnected) return false

      // Keep the existing recommendations rather than emptying the carousel
      if (!products.length) return false

      const slides = await this.buildSlides(products)
      if (token !== this.refreshToken || !this.isConnected || !slides) {
        return false
      }

      const list = this.querySelector('.splide__list')
      if (!list) return false

      this.items = nextItems
      this.products = products
      list.innerHTML = slides
      this.slider?.refresh()
      return true
    }

    async fetchRecommendations(items = this.items) {
      const options = { method: 'GET', headers: { accept: 'application/json' } }
      try {
        let url = new URL(
          `https://${this.siteId}.a.searchspring.io/boost/${this.siteId}/recommend?siteId=${this.siteId}`
        )

        url.searchParams.append('beacon', 'true')

        if (this.recommendation_type) {
          url.searchParams.append('tags', this.recommendation_type)
        }

        if (items && items !== '') {
          url.searchParams.append(this.productKey, items)
        }

        if (this.exclusions) {
          url.searchParams.append('blockedItems', this.exclusions)
        }

        // Cookie-sourced personalization; skip cart when mini-cart already set it via items
        this.appendPersonalizationParams(url, {
          skipCart: this.productKey === 'cart' && !!items,
        })

        const response = await fetch(url.toString(), options)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        if (
          Array.isArray(data) &&
          data.length > 0 &&
          typeof data[0] === 'object' &&
          Array.isArray(data[0].results)
        ) {
          this.responseId = data[0].responseId
          return data[0].results.slice(0, this.limit)
        }

        console.error('Invalid API response structure:', data)
      } catch (err) {
        console.error('Error fetching recommendations:', err)
      }

      return []
    }

    render() {
      if (!this.isConnected) return

      const container = this.querySelector('.product-recommendations')

      const content = document.createElement('div')
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      )
      container.appendChild(content)
    }

    async buildSlides(products) {
      const slides = await Promise.all(
        products.map((product) => this.buildSlide(product))
      )
      return slides.join('')
    }

    async buildSlide(product) {
      const productUrl = product.mappings?.core?.url
      if (!productUrl) return ''

      try {
        const response = await fetch(
          `${productUrl}?section_id=${this.sectionName}`
        )
        if (!response.ok) return ''

        const markup = await response.text()
        const fresh = document.createElement('div')
        fresh.innerHTML = markup

        const productCard = fresh.querySelector('[data-api-content]')
        if (!productCard) return ''

        if (this.placement === 'mini-cart') {
          productCard.querySelector('product-form')?.remove()
        }

        return `<li class="splide__slide">${productCard.innerHTML}</li>`
      } catch (err) {
        console.error('Error fetching recommendation card:', err)
        return ''
      }
    }

    initCarousel() {
      this.slider = new Splide(
        this.querySelector('.js-recommendations-slider'),
        {
          gap: 20,
          // arrows: this.settings?.show_arrows || false,
          pagination: this.settings?.show_dots || false,
          mediaQuery: 'min',
          perPage: this.settings?.slides_num_mobile
            ? parseInt(this.settings.slides_num_mobile)
            : 2,
          breakpoints: {
            1024: {
              perPage: this.settings?.slides_num
                ? parseInt(this.settings.slides_num)
                : 2,
            },
          },
        }
      )
      this.slider.mount()
    }

    readCookie(name) {
      const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1')
      const match = document.cookie.match(
        new RegExp(`(?:^|; )${escaped}=([^;]*)`)
      )
      if (!match) return ''
      const raw = match[1].trim()
      try {
        return decodeURIComponent(raw)
      } catch {
        return ''
      }
    }

    normalizeCommaSeparatedIds(raw) {
      if (!raw) return ''
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .join(',')
    }

    /** Web Pixel cookies: ssShopperId → shopper, ssCartProducts → cart, ssViewedProducts → lastViewed */
    appendPersonalizationParams(url, { skipCart = false } = {}) {
      const shopper =
        this.readCookie('ssShopperId') || this.shopperId || ''
      const cart = this.normalizeCommaSeparatedIds(
        this.readCookie('ssCartProducts')
      )
      const lastViewed = this.normalizeCommaSeparatedIds(
        this.readCookie('ssViewedProducts')
      )

      if (shopper) {
        url.searchParams.set('shopper', shopper)
      }
      if (cart && !skipCart) {
        url.searchParams.set('cart', cart)
      }
      if (lastViewed) {
        url.searchParams.set('lastViewed', lastViewed)
      }
    }
  }

  customElements.define(
    'searchspring-recommendations',
    SearchspringRecommendations
  )
}
